/**
 * Räumliches Diäten-Modell — FE-Seite.
 *
 * Der OCR-Spatial-Prepass (ocr/app.py `_expense_spatial_prepass`, PR #40)
 * schreibt nach erfolgreichem Template-Match `data.meta.spatial`:
 *   - rotation_applied_cw: 0|90|180|270 — wie der Scan aufgerichtet wurde.
 *     Die Preview MUSS mit `?rotate=<dieser Wert>` angefragt werden, damit
 *     Preview-Frame == Spatial-Frame (sonst zeigen die Prozente daneben).
 *   - row_boundaries_pct: 23 projizierte Zeilen-Grenzen (0..1, y/Bildhöhe)
 *   - col_lines_pct: 10 projizierte Spalten-Linien (0..1, x/Bildbreite)
 *   - header_bbox_pct: Monat/Dienstnehmer-Band
 *
 * Wenn vorhanden, sind diese Werte homography-projiziert (pixelgenau pro
 * Scan) und ersetzen die statische Heuristik aus expenseCellBbox.ts.
 */
import type { BboxPct, ExpenseFieldName } from "./expenseCellBbox";

/** Spalten-Reihenfolge des Diäten-Formulars = Reihenfolge der col_lines. */
export const EXPENSE_COL_ORDER: ExpenseFieldName[] = [
  "tag", "datum", "kz_nr", "kunde", "zm_abzug", "m_verr", "ort", "abfahrt", "rueckkehr",
];

export interface ExpenseSpatial {
  rotationAppliedCw: 0 | 90 | 180 | 270;
  /** Zeilen-Grenzen in % (0–100) der (aufgerichteten) Bildhöhe — n+1 Grenzen = n Zeilen. */
  rowBoundariesPct: number[];
  /** Spalten-Linien in % (0–100) der Bildbreite — 10 Linien = 9 Spalten. */
  colLinesPct: number[];
  inliers: number | null;
}

/** Liest meta.spatial aus card.data — null wenn nicht vorhanden/unbrauchbar. */
export function spatialFromData(data: unknown): ExpenseSpatial | null {
  const sp = (data as any)?.meta?.spatial;
  if (!sp) return null;
  const rows = Array.isArray(sp.row_boundaries_pct) ? sp.row_boundaries_pct : [];
  const cols = Array.isArray(sp.col_lines_pct) ? sp.col_lines_pct : [];
  // Mindestanforderung: 2+ Zeilen-Grenzen und alle 10 Spalten-Linien —
  // sonst ist die Projektion unvollständig und die Heuristik ehrlicher.
  if (rows.length < 3 || cols.length < EXPENSE_COL_ORDER.length + 1) return null;
  const rot = Number(sp.rotation_applied_cw ?? 0);
  return {
    rotationAppliedCw: (rot === 90 || rot === 180 || rot === 270 ? rot : 0) as ExpenseSpatial["rotationAppliedCw"],
    rowBoundariesPct: rows.map((v: number) => v * 100),
    colLinesPct: cols.map((v: number) => v * 100),
    inliers: typeof sp.inliers === "number" ? sp.inliers : null,
  };
}

/** Max. Datenzeilen laut Spatial-Geometrie (Grenzen − 1). */
export function spatialMaxRows(sp: ExpenseSpatial): number {
  return sp.rowBoundariesPct.length - 1;
}

/** Pixelgenaue Bbox (Prozent-Frame der Preview) für (Zeilen-Index, Feld). */
export function spatialCellBboxPct(
  sp: ExpenseSpatial, rowIndex: number, field: ExpenseFieldName,
): BboxPct | null {
  if (rowIndex < 0 || rowIndex >= spatialMaxRows(sp)) return null;
  const ci = EXPENSE_COL_ORDER.indexOf(field);
  if (ci < 0 || ci + 1 >= sp.colLinesPct.length) return null;
  const top = sp.rowBoundariesPct[rowIndex];
  const bottom = sp.rowBoundariesPct[rowIndex + 1];
  const left = sp.colLinesPct[ci];
  const right = sp.colLinesPct[ci + 1];
  if (top === undefined || bottom === undefined || left === undefined || right === undefined) return null;
  return {
    topPct: top,
    leftPct: left,
    widthPct: right - left,
    heightPct: bottom - top,
  };
}

/** Ganze Zeile (alle Spalten) — Pendant zu expenseRowBboxPct. */
export function spatialRowBboxPct(sp: ExpenseSpatial, rowIndex: number): BboxPct | null {
  if (rowIndex < 0 || rowIndex >= spatialMaxRows(sp)) return null;
  const top = sp.rowBoundariesPct[rowIndex];
  const bottom = sp.rowBoundariesPct[rowIndex + 1];
  const left = sp.colLinesPct[0];
  const right = sp.colLinesPct[sp.colLinesPct.length - 1];
  if (top === undefined || bottom === undefined || left === undefined || right === undefined) return null;
  return {
    topPct: top,
    leftPct: left,
    widthPct: right - left,
    heightPct: bottom - top,
  };
}

/** In welchem Frame hat der LESER die Seite gelesen? Die Vorschau muss
 *  denselben Frame zeigen, sonst stimmen Bild und Overlay nicht ueberein.
 *
 *  GEMESSEN 2026-08-12 (Thomas: „die orientierung ist um 90° verdreht"): Drei
 *  Fahrtenbuecher wurden beim Upload faelschlich quergelegt. Nach der Korrektur
 *  ueber die Dreh-Schaltflaeche stand die Nutzer-Wahl in
 *  tier_path.rotation_resolved_by_user, der Leser las damit korrekt (2790 km
 *  statt nichts) — die VORSCHAU nahm aber weiterhin nur
 *  meta.spatial.rotation_applied_cw und blieb dadurch quer. Wer die Drehung
 *  korrigiert, bekam sie also nie zu sehen.
 *
 *  Reihenfolge = Verlaesslichkeit: raeumliches Modell schlaegt die vom Leser
 *  angewandte Drehung, diese schlaegt die reine Nutzer-Wahl. */
export function leseFrameDrehung(data: unknown): 0 | 90 | 180 | 270 {
  const d = data as any;
  const kandidaten = [
    d?.meta?.spatial?.rotation_applied_cw,
    d?.meta?.manual_orientation_cw,
    d?.tier_path?.rotation_resolved_by_user,
  ];
  for (const k of kandidaten) {
    const n = Number(k);
    if (n === 90 || n === 180 || n === 270) return n;
    if (n === 0) return 0;
  }
  return 0;
}
